/**
 * 라이브러리 받기 메뉴 — 실제로 있거나 지금 다시 만들 수 있는 항목만 고른다.
 * 이미지형에서 텍스트형으로 변환하는 항목은 만들지 않는다.
 */

import { artifactRecordsFromConversation, isWorksheetExportEnabled } from './artifactHistory';
import { hasCanvas } from './canvasSchema';
import {
  familyHasVersion,
  familyOutline,
  familyVersions,
  filenameForFormat,
  parseOutline,
  pickLibraryRemakeOutline,
  recordsFromFamilyVersions,
} from './libraryFamily';
import { freeDesignRef } from './freeDesignOutline';

export const LIBRARY_TEXT_HINT = '텍스트형 파일은 대화에서 저장하면 여기서도 받을 수 있어요';

const CATEGORY_BY_TYPE = {
  ppt: 'ppt_maker',
  worksheet: 'worksheet_gen',
};

const hasSlides = (outline) => Array.isArray(outline?.slides) && outline.slides.length > 0;
const hasWorksheetBody = (outline) => (
  hasSlides(outline)
  || (Array.isArray(outline?.questions) && outline.questions.length > 0)
  || (Array.isArray(outline?.sections) && outline.sections.length > 0)
);

const latestMatching = (records, predicate) => (records || []).find((record) => predicate(record)) || null;

/**
 * 옵션의 저장 파일명. 대표 filename 이 있어도 확장자는 요청 형식(fallback = '.hwpx' 등)으로 맞춘다.
 * 그대로 쓰면 `…_정답해설.pdf` 가 HWPX 옵션에 붙어 한글에서 열리지 않는 파일이 저장된다.
 */
const filenameFrom = (item, fallback) => {
  const format = String(fallback || '').replace(/^\./, '').toLowerCase();
  if (item?.filename) return format ? filenameForFormat(item.filename, format) : item.filename;
  const title = String(item?.title || 'whatquiz').replace(/[/\\:*?"<>|]/g, '_').slice(0, 50);
  return `${title}${fallback}`;
};

const collectRecords = (item, conversation) => {
  const category = CATEGORY_BY_TYPE[item?.content_type];
  const fromFamily = recordsFromFamilyVersions(item);
  const fromConversation = category ? artifactRecordsFromConversation(conversation, category) : [];
  const extras = [];
  if (item?.artifact && item.artifact.format && item.artifact.downloadUrl) {
    extras.push(item.artifact);
  }
  return [...fromFamily, ...extras, ...fromConversation];
};

export const hasTextVersionEvidence = (item, conversation, records) => {
  if (familyHasVersion(item, 'text')) return true;
  if ((records || []).some((record) => record.sourceMode === 'text')) return true;
  if (item?.artifact?.sourceMode === 'text') return true;
  if (item?.content_type === 'ppt' && conversation?.pptDownloadMode === 'text') return true;
  if (item?.content_type === 'worksheet') {
    if (conversation?.worksheetDownloadUrl && !(conversation?.worksheetOriginalUrls || []).length) {
      return true;
    }
  }
  return false;
};

export const hasImageVersionEvidence = (item, conversation, records) => {
  if (familyHasVersion(item, 'image')) return true;
  if ((records || []).some((record) => record.sourceMode === 'image')) return true;
  if (item?.artifact?.sourceMode === 'image') return true;
  if (item?.content_type === 'ppt') {
    if (conversation?.pptDownloadMode === 'image') return true;
    if ((conversation?.pptOriginalUrls || []).length > 0) return true;
  }
  if (item?.content_type === 'worksheet' && (conversation?.worksheetOriginalUrls || []).length > 0) {
    return true;
  }
  // 미리보기 장수만으로 이미지 버전을 단정하지 않는다: 텍스트 학습지도 학생용 쪽 PNG 를
  // 여러 장 가질 수 있다(시드·공유 스냅샷). 텍스트 칸에 파일이나 개요가 있으면 미리보기는 그 칸의 것이다.
  if ((item?.preview_urls || []).length > 1) {
    const textSlot = familyVersions(item).text;
    const textHasBody = Boolean(
      textSlot && (Object.keys(textSlot.file_urls || {}).length || textSlot.file_url || textSlot.outline),
    );
    if (!textHasBody) return true;
  }
  return false;
};

const outlineFor = (item, conversation) => pickLibraryRemakeOutline(item, conversation);

/** 이 자료에 텍스트형 PPT 덱이 있으면 그 참조. 대화 개요 → 라이브러리 텍스트 칸 → 대표 개요 순으로 찾는다. */
export const libraryFreeDesignRef = (item, conversation = null) => {
  if (item?.content_type !== 'ppt') return null;
  return freeDesignRef(outlineFor(item, conversation))
    || freeDesignRef(parseOutline(conversation?.pptOutline))
    || freeDesignRef(familyOutline(item, 'text'))
    || freeDesignRef(parseOutline(item?.outline));
};

const canRemakeTextPpt = (item, conversation) => {
  const outline = outlineFor(item, conversation);
  return hasSlides(outline);
};

const canRemakeTextWorksheet = (item, conversation) => {
  const outline = outlineFor(item, conversation);
  return hasWorksheetBody(outline);
};

const option = (fields) => ({
  kind: 'direct',
  remake: null,
  url: '',
  artifactId: null,
  ...fields,
});

const buildPptOptions = ({ item, conversation, records, hasText, hasImage }) => {
  const options = [];
  const textRecord = latestMatching(records, (record) => record.sourceMode === 'text' && record.format === 'pptx');
  const imagePptx = latestMatching(records, (record) => record.sourceMode === 'image' && record.format === 'pptx');
  const imagePdf = latestMatching(records, (record) => record.sourceMode === 'image' && record.format === 'pdf');
  const outline = outlineFor(item, conversation);
  const design = libraryFreeDesignRef(item, conversation);

  if (design) {
    options.push(option({
      id: 'ppt-design',
      label: '텍스트형 PPT',
      description: '파워포인트에서 글자를 바로 고칠 수 있어요',
      format: 'pptx',
      sourceMode: 'text',
      kind: 'remake',
      remake: { type: 'ppt-free-design', deckId: design.deck_id, format: 'pptx_editable' },
      filename: filenameFrom(item, '.pptx'),
    }));
    options.push(option({
      id: 'ppt-design-image',
      label: '텍스트형 PPT (그림 파일)',
      description: '어디서 열어도 모양이 그대로예요. 글자는 못 고쳐요',
      format: 'pptx',
      sourceMode: 'text',
      kind: 'remake',
      remake: { type: 'ppt-free-design', deckId: design.deck_id, format: 'pptx' },
      filename: filenameFrom(item, '.pptx'),
    }));
    options.push(option({
      id: 'ppt-design-pdf',
      label: '텍스트형 PDF',
      description: '인쇄하거나 나눠 주기 좋아요',
      format: 'pdf',
      sourceMode: 'text',
      kind: 'remake',
      remake: { type: 'ppt-free-design', deckId: design.deck_id, format: 'pdf' },
      filename: filenameFrom(item, '.pdf'),
    }));
  } else if (hasText) {
    if (textRecord?.downloadUrl) {
      options.push(option({
        id: 'ppt-text',
        label: '텍스트형 PPT',
        description: 'PowerPoint에서 글자를 고칠 수 있어요',
        format: 'pptx',
        sourceMode: 'text',
        url: textRecord.downloadUrl,
        filename: textRecord.filename || filenameFrom(item, '.pptx'),
        artifactId: textRecord.id || null,
      }));
    } else if (canRemakeTextPpt(item, conversation)) {
      options.push(option({
        id: 'ppt-text',
        label: '텍스트형 PPT',
        description: 'PowerPoint에서 글자를 고칠 수 있어요',
        format: 'pptx',
        sourceMode: 'text',
        kind: 'remake',
        remake: { type: 'ppt-text', preferCanvas: Boolean(outline && (outline.slides || []).some(hasCanvas)) },
        filename: filenameFrom(item, '.pptx'),
      }));
    }
  }

  if (hasImage) {
    const imageUrl = imagePptx?.downloadUrl || familyVersions(item).image?.file_url || item.download_url || '';
    if (imageUrl) {
      options.push(option({
        id: 'ppt-image',
        label: '이미지형 PPT',
        description: '그림으로 된 슬라이드 파일이에요',
        format: 'pptx',
        sourceMode: 'image',
        url: imageUrl,
        filename: imagePptx?.filename || filenameFrom(item, '.pptx'),
        artifactId: imagePptx?.id || null,
      }));
    }
    if (imagePdf?.downloadUrl) {
      options.push(option({
        id: 'ppt-image-pdf',
        label: '이미지형 PDF',
        description: '그림 슬라이드를 PDF로 받아요',
        format: 'pdf',
        sourceMode: 'image',
        url: imagePdf.downloadUrl,
        filename: imagePdf.filename || filenameFrom(item, '.pdf'),
        artifactId: imagePdf.id || null,
      }));
    }
  }

  return options;
};

const buildWorksheetOptions = ({ item, conversation, records, hasText, hasImage, exportEnabled }) => {
  const options = [];
  const hwpx = latestMatching(records, (record) => record.sourceMode === 'text' && record.format === 'hwpx');
  const textPdf = latestMatching(records, (record) => record.sourceMode === 'text' && record.format === 'pdf');
  const docx = latestMatching(records, (record) => record.sourceMode === 'text' && record.format === 'docx');
  const imagePdf = latestMatching(records, (record) => record.sourceMode === 'image' && record.format === 'pdf');

  if (hasText) {
    if (hwpx?.downloadUrl) {
      options.push(option({
        id: 'ws-hwpx',
        label: '한글 (HWPX)',
        description: '한글에서 바로 고쳐 쓸 수 있어요',
        format: 'hwpx',
        sourceMode: 'text',
        url: hwpx.downloadUrl,
        filename: hwpx.filename || filenameFrom(item, '.hwpx'),
        artifactId: hwpx.id || null,
      }));
    } else if (exportEnabled && canRemakeTextWorksheet(item, conversation)) {
      options.push(option({
        id: 'ws-hwpx',
        label: '한글 (HWPX)',
        description: '한글에서 바로 고쳐 쓸 수 있어요',
        format: 'hwpx',
        sourceMode: 'text',
        kind: 'remake',
        remake: { type: 'ws-export', format: 'hwpx' },
        filename: filenameFrom(item, '.hwpx'),
      }));
    }

    if (textPdf?.downloadUrl) {
      options.push(option({
        id: 'ws-pdf-text',
        label: 'PDF (글자 선택 가능)',
        description: '인쇄하거나 글자를 복사할 수 있어요',
        format: 'pdf',
        sourceMode: 'text',
        url: textPdf.downloadUrl,
        filename: textPdf.filename || filenameFrom(item, '.pdf'),
        artifactId: textPdf.id || null,
      }));
    } else if (canRemakeTextWorksheet(item, conversation)) {
      options.push(option({
        id: 'ws-pdf-text',
        label: 'PDF (글자 선택 가능)',
        description: '인쇄하거나 글자를 복사할 수 있어요',
        format: 'pdf',
        sourceMode: 'text',
        kind: 'remake',
        remake: { type: 'ws-canvas-pdf' },
        filename: filenameFrom(item, '.pdf'),
      }));
    }

    if (docx?.downloadUrl) {
      options.push(option({
        id: 'ws-docx',
        label: '워드 (DOCX)',
        description: '워드에서 고쳐 쓸 수 있어요',
        format: 'docx',
        sourceMode: 'text',
        url: docx.downloadUrl,
        filename: docx.filename || filenameFrom(item, '.docx'),
        artifactId: docx.id || null,
      }));
    } else if (exportEnabled && canRemakeTextWorksheet(item, conversation)) {
      options.push(option({
        id: 'ws-docx',
        label: '워드 (DOCX)',
        description: '워드에서 고쳐 쓸 수 있어요',
        format: 'docx',
        sourceMode: 'text',
        kind: 'remake',
        remake: { type: 'ws-export', format: 'docx' },
        filename: filenameFrom(item, '.docx'),
      }));
    }
  }

  if (hasImage) {
    const imageUrl = imagePdf?.downloadUrl || familyVersions(item).image?.file_url || item.download_url || '';
    if (imageUrl) {
      options.push(option({
        id: 'ws-pdf-image',
        label: '이미지형 PDF',
        description: '그림으로 된 학습지예요',
        format: 'pdf',
        sourceMode: 'image',
        url: imageUrl,
        filename: imagePdf?.filename || filenameFrom(item, '.pdf'),
        artifactId: imagePdf?.id || null,
      }));
    }
  }

  return options;
};

const fallbackSingleOption = (item) => {
  const url = item?.download_url || item?.thumbnail_url || '';
  if (!url && !(item?.content_type === 'other' && item?.artifact_id)) return [];
  if (item?.content_type === 'other') {
    return [option({
      id: 'other',
      label: '파일 받기',
      description: '이 자료를 그대로 받아요',
      format: item.artifact_format || 'file',
      sourceMode: null,
      url,
      filename: item.filename || filenameFrom(item, ''),
      remake: item.artifact_id ? { type: 'chat-artifact', fileId: item.artifact_id } : null,
      kind: item.artifact_id ? 'remake' : 'direct',
    })];
  }
  if (item?.content_type === 'image') {
    return [option({
      id: 'image',
      label: '이미지 받기',
      description: '이 그림을 받아요',
      format: 'png',
      sourceMode: null,
      url,
      filename: filenameFrom(item, '.png'),
    })];
  }
  const ext = item?.content_type === 'ppt' ? '.pptx' : '.pdf';
  return [option({
    id: 'stored',
    label: '다운로드',
    description: '저장된 파일을 받아요',
    format: item?.content_type === 'ppt' ? 'pptx' : 'pdf',
    sourceMode: null,
    url,
    filename: filenameFrom(item, ext),
  })];
};

/**
 * @returns {{ options: object[], textHint: string|null }}
 */
export function buildLibraryDownloadOptions({
  item,
  conversation = null,
  exportEnabled = isWorksheetExportEnabled(),
} = {}) {
  if (!item) return { options: [], textHint: null };
  if (item.content_type === 'other' || item.content_type === 'image') {
    return { options: fallbackSingleOption(item), textHint: null };
  }

  const records = collectRecords(item, conversation);
  const hasText = hasTextVersionEvidence(item, conversation, records);
  const hasImage = hasImageVersionEvidence(item, conversation, records);

  if (!conversation && !hasText && !hasImage && !item?.versions && !item?.versions_summary) {
    return { options: fallbackSingleOption(item), textHint: null };
  }

  const options = item.content_type === 'ppt'
    ? buildPptOptions({ item, conversation, records, hasText, hasImage })
    : buildWorksheetOptions({
      item, conversation, records, hasText, hasImage, exportEnabled,
    });

  if (options.length === 0) {
    const fallback = fallbackSingleOption(item);
    return {
      options: fallback,
      textHint: hasImage && !hasText ? LIBRARY_TEXT_HINT : null,
    };
  }

  return {
    options,
    textHint: hasImage && !hasText ? LIBRARY_TEXT_HINT : null,
  };
}

export const shouldOpenDownloadMenu = (options) => Array.isArray(options) && options.length > 1;

export function canResumeLibraryItem(item, conversation) {
  if (!item?.conversation_id) return false;
  if (!conversation) return false;
  if (item.content_type === 'other' || item.content_type === 'image') {
    return Boolean(conversation.id);
  }
  const records = collectRecords(item, conversation);
  if (records.length > 0) return true;
  if (item.content_type === 'ppt' && (conversation.pptOutline || conversation.pptDownloadUrl)) return true;
  if (item.content_type === 'worksheet' && (
    conversation.worksheetOutline
    || conversation.worksheetCanvas
    || conversation.worksheetDownloadUrl
  )) return true;
  return false;
}

export function pickResumeArtifactId(item, conversation) {
  const records = collectRecords(item, conversation);
  return records.find((record) => record?.id)?.id || item?.artifact?.id || null;
}
